import { useState, useEffect, useCallback } from "react";
import {
  getAllFormEntries,
  getFormEntries,
  deleteFormEntry,
  clearFormEntries,
} from "@/utils/formEntryStore";
import {
  fetchFormEntries,
  deleteFormEntryFromSupabase,
  type SupabaseFormEntry,
} from "@/utils/submitFormToSupabase";

const FORM_FILTERS = [
  { id: "all", label: "All Forms" },
  { id: "contact", label: "Send Us a Message" },
  { id: "application", label: "Artist Application" },
  { id: "notify", label: "Get Notified" },
  { id: "career", label: "Career Application" },
];

interface UnifiedEntry {
  id: string;
  formId: string;
  formLabel: string;
  submittedAt: string;
  fields: { label: string; value: string }[];
  source: "supabase" | "local";
}

function toUnified(e: SupabaseFormEntry): UnifiedEntry {
  return {
    id: e.id,
    formId: e.form_id,
    formLabel: e.form_label,
    submittedAt: e.submitted_at,
    fields: e.fields,
    source: "supabase",
  };
}

function localToUnified(e: { id: string; formId: string; formLabel: string; submittedAt: string; fields: { label: string; value: string }[] }): UnifiedEntry {
  return {
    id: e.id,
    formId: e.formId,
    formLabel: e.formLabel,
    submittedAt: e.submittedAt,
    fields: e.fields,
    source: "local",
  };
}

export default function FormEntriesTab({ isDark }: { isDark: boolean }) {
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [entries, setEntries] = useState<UnifiedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [supabaseCounts, setSupabaseCounts] = useState<Record<string, number>>({});

  const cardBg = isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200";
  const headingColor = isDark ? "text-white" : "text-gray-800";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const rowHover = isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-50";

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const supabaseEntries = await fetchFormEntries(
        selectedFilter === "all" ? undefined : selectedFilter
      );
      const unified = supabaseEntries.map(toUnified);

      const localEntries = selectedFilter === "all"
        ? getAllFormEntries()
        : getFormEntries(selectedFilter);
      const supabaseIds = new Set(unified.map((e) => e.id));
      const localOnly = localEntries
        .filter((e) => !supabaseIds.has(e.id))
        .map(localToUnified);

      const combined = [...unified, ...localOnly].sort(
        (a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
      );
      setEntries(combined);

      const allSupabase = await fetchFormEntries();
      const counts: Record<string, number> = { all: allSupabase.length };
      FORM_FILTERS.forEach((f) => {
        if (f.id !== "all") {
          counts[f.id] = allSupabase.filter((e) => e.form_id === f.id).length;
        }
      });
      setSupabaseCounts(counts);
    } catch {
      const localEntries = selectedFilter === "all"
        ? getAllFormEntries()
        : getFormEntries(selectedFilter);
      setEntries(localEntries.map(localToUnified));
      const counts: Record<string, number> = { all: getAllFormEntries().length };
      FORM_FILTERS.forEach((f) => {
        if (f.id !== "all") counts[f.id] = getFormEntries(f.id).length;
      });
      setSupabaseCounts(counts);
    } finally {
      setLoading(false);
    }
  }, [selectedFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleDelete = async (entry: UnifiedEntry) => {
    if (entry.source === "supabase") {
      await deleteFormEntryFromSupabase(entry.id);
    } else {
      deleteFormEntry(entry.id);
    }
    refresh();
  };

  const handleClearAll = async () => {
    clearFormEntries(selectedFilter === "all" ? undefined : selectedFilter);
    if (selectedFilter === "all") {
      const all = await fetchFormEntries();
      for (const e of all) {
        await deleteFormEntryFromSupabase(e.id);
      }
    } else {
      const filtered = await fetchFormEntries(selectedFilter);
      for (const e of filtered) {
        await deleteFormEntryFromSupabase(e.id);
      }
    }
    setConfirmClear(false);
    refresh();
  };

  const handleExportCSV = () => {
    if (entries.length === 0) return;
    const allLabels = Array.from(
      new Set(entries.flatMap((e) => e.fields.map((f) => f.label)))
    );
    const header = ["Form", "Date", "Source", ...allLabels].join(",");
    const rows = entries.map((e) => {
      const date = new Date(e.submittedAt).toLocaleString("en-US");
      const fieldMap: Record<string, string> = {};
      e.fields.forEach((f) => { fieldMap[f.label] = f.value; });
      const cols = allLabels.map((l) => `"${(fieldMap[l] ?? "").replace(/"/g, '""')}"`);
      return [`"${e.formLabel}"`, `"${date}"`, `"${e.source}"`, ...cols].join(",");
    });
    const csv = [header, ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `makers-alley-entries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportText = (entry: UnifiedEntry) => {
    const date = new Date(entry.submittedAt).toLocaleString("en-US", {
      month: "long", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
    const lines = [
      `=== ${entry.formLabel} ===`,
      `Submitted: ${date}`,
      `Source: ${entry.source}`,
      ``,
      ...entry.fields.map((f) => `${f.label}: ${f.value}`),
      ``,
    ];
    const text = lines.join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `entry-${entry.id}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className={`text-xl font-bold mb-1 ${headingColor}`} style={{ fontFamily: "'Playfair Display', serif" }}>
            Form Entries
          </h2>
          <p className={`text-sm ${subColor}`}>
            All submissions from visitors worldwide — synced from Supabase in real-time.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => refresh()}
            className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap ${isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            <i className={`${loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}`}></i> {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            onClick={handleExportCSV}
            disabled={entries.length === 0}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap bg-green-500 hover:bg-green-600 text-white disabled:opacity-40"
          >
            <i className="ri-download-line"></i> Export CSV
          </button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        {FORM_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setSelectedFilter(f.id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors ${
              selectedFilter === f.id
                ? "bg-pink-500 text-white"
                : isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {f.label}
            {f.id !== "all" && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                selectedFilter === f.id ? "bg-white/20" : isDark ? "bg-gray-600" : "bg-gray-200"
              }`}>
                {supabaseCounts[f.id] ?? 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Supabase sync info banner */}
      <div className={`rounded-2xl border p-4 flex items-start gap-3 ${isDark ? "bg-green-900/20 border-green-800/40" : "bg-green-50 border-green-200"}`}>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-green-100 flex-shrink-0">
          <i className="ri-database-2-line text-green-600 text-sm"></i>
        </div>
        <div>
          <div className={`font-bold text-sm ${headingColor}`}>Live from Supabase</div>
          <div className={`text-xs mt-0.5 ${subColor}`}>
            Form submissions from <strong className={headingColor}>any visitor, anywhere in the world</strong> appear here instantly — no admin action needed. Contact forms, artist applications, and email signups all sync automatically.
          </div>
        </div>
      </div>

      {/* Entries table */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`}>
        <div className={`px-5 py-4 border-b flex items-center justify-between ${isDark ? "border-gray-700" : "border-gray-100"}`}>
          <h3 className={`font-bold text-sm ${headingColor}`}>
            {entries.length} {entries.length === 1 ? "Entry" : "Entries"}
            {selectedFilter !== "all" && ` — ${FORM_FILTERS.find((f) => f.id === selectedFilter)?.label}`}
          </h3>
          {entries.length > 0 && (
            confirmClear ? (
              <div className="flex items-center gap-2">
                <span className={`text-xs ${subColor}`}>Clear all?</span>
                <button onClick={handleClearAll} className="text-xs font-bold text-red-400 hover:text-red-600 cursor-pointer whitespace-nowrap">Yes, clear</button>
                <button onClick={() => setConfirmClear(false)} className={`text-xs cursor-pointer whitespace-nowrap ${subColor}`}>Cancel</button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmClear(true)}
                className={`text-xs cursor-pointer whitespace-nowrap transition-colors ${isDark ? "text-gray-500 hover:text-red-400" : "text-gray-400 hover:text-red-400"}`}
              >
                <i className="ri-delete-bin-line mr-1"></i>Clear
              </button>
            )
          )}
        </div>

        {loading ? (
          <div className="px-5 py-12 text-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-pink-400 mb-3 block"></i>
            <p className={`text-sm ${subColor}`}>Loading entries from Supabase...</p>
          </div>
        ) : entries.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-3">
              <i className="ri-inbox-line text-gray-400 text-xl"></i>
            </div>
            <p className={`text-sm font-semibold mb-1 ${headingColor}`}>No entries yet</p>
            <p className={`text-xs ${subColor}`}>Submissions will appear here as soon as visitors fill out a form — from any browser, anywhere.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {entries.map((entry) => {
              const isExpanded = expandedId === entry.id;
              const date = new Date(entry.submittedAt).toLocaleString("en-US", {
                month: "short", day: "numeric", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              });
              const preview = entry.fields[0]?.value ?? "";
              return (
                <div key={entry.id} className={`transition-colors ${isDark ? "divide-gray-700" : ""}`}>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                    className={`w-full flex items-center gap-4 px-5 py-4 text-left cursor-pointer transition-colors ${rowHover}`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                      entry.formId === "application" ? "bg-green-100" :
                      entry.formId === "contact" ? "bg-pink-100" :
                      entry.formId === "career" ? "bg-sky-100" : "bg-amber-100"
                    }`}>
                      <i className={`text-sm ${
                        entry.formId === "application" ? "ri-palette-line text-green-600" :
                        entry.formId === "contact" ? "ri-message-3-line text-pink-500" :
                        entry.formId === "career" ? "ri-briefcase-line text-sky-600" : "ri-notification-3-line text-amber-600"
                      }`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold ${headingColor}`}>{entry.formLabel}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isDark ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"
                        }`}>{date}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                          entry.source === "supabase"
                            ? isDark ? "bg-green-900/40 text-green-400" : "bg-green-50 text-green-600"
                            : isDark ? "bg-gray-700 text-gray-500" : "bg-gray-100 text-gray-400"
                        }`}>
                          {entry.source === "supabase" ? "Synced" : "Local only"}
                        </span>
                      </div>
                      <p className={`text-xs truncate mt-0.5 ${subColor}`}>{preview}</p>
                    </div>
                    <i className={`${isExpanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 flex-shrink-0`}></i>
                  </button>

                  {isExpanded && (
                    <div className={`px-5 pb-5 border-t ${isDark ? "border-gray-700 bg-gray-700/30" : "border-gray-50 bg-gray-50/50"}`}>
                      <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {entry.fields.map((field, fi) => (
                          <div key={fi} className={`rounded-xl p-3 ${isDark ? "bg-gray-700" : "bg-white"}`}>
                            <div className={`text-xs font-semibold uppercase tracking-wide mb-1 ${subColor}`}>{field.label}</div>
                            <div className={`text-sm break-words ${headingColor}`}>{field.value || <span className={subColor}>—</span>}</div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 mt-4">
                        <button
                          onClick={() => handleExportText(entry)}
                          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                            isDark ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          <i className="ri-file-text-line"></i> Export as Text
                        </button>
                        <button
                          onClick={() => handleDelete(entry)}
                          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer transition-colors whitespace-nowrap text-red-400 hover:text-red-600 hover:bg-red-50"
                        >
                          <i className="ri-delete-bin-line"></i> Delete Entry
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}